import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { Target, Eye, Heart, Users, Award, Globe2 } from "lucide-react";

const IMAGES = {
  doctor1: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/24ef8d8e53f3568e65962aae65157dfc97dad17d6df9af2c0097ea910361284d.png",
  hospital: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/58694fc3bcfb7049a8a6ea274c609a8ea917769607bf303b743287952048c4c9.png",
};

const valueIcons = [Heart, Award, Globe2, Users];
const valueKeys = ["patientFirst", "qualityAssured", "globalReach", "dedicatedTeam"];

export default function AboutPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="about-page">
      <SEO title="About Us" description="Learn about Flarial MediTours - your trusted partner in medical tourism connecting international patients with world-class healthcare in Kerala, India." path="/about" keywords="about Flarial MediTours, medical tourism company India, healthcare partner" />
      {/* Hero */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <span className="overline">{t("about.overline")}</span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
              {t("about.title")} <span className="font-medium text-[#00A4A9]">{t("about.titleHighlight")}</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl mb-12">
              {t("about.desc")}
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <FadeIn delay={100}>
              <img src={IMAGES.doctor1} alt="Doctor consulting patient" className="rounded-2xl w-full h-80 object-cover" />
            </FadeIn>
            <FadeIn delay={200}>
              <img src={IMAGES.hospital} alt="Modern hospital facility" className="rounded-2xl w-full h-80 object-cover" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeIn>
              <div className="bg-[#e0f2fe] rounded-2xl p-10" data-testid="mission-card">
                <div className="w-12 h-12 rounded-xl bg-[#00A4A9] flex items-center justify-center mb-5">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-2xl font-medium text-[#03045e] mb-4">{t("about.mission")}</h3>
                <p className="text-slate-600 leading-relaxed">{t("about.missionDesc")}</p>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <div className="bg-[#F1F5F9] rounded-2xl p-10" data-testid="vision-card">
                <div className="w-12 h-12 rounded-xl bg-[#03045e] flex items-center justify-center mb-5">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-2xl font-medium text-[#03045e] mb-4">{t("about.vision")}</h3>
                <p className="text-slate-600 leading-relaxed">{t("about.visionDesc")}</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("about.values")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("about.valuesTitle")}
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
            {valueKeys.map((key, i) => {
              const Icon = valueIcons[i];
              return (
              <FadeIn key={key} delay={i * 80}>
                <div className="bg-white border border-slate-200 rounded-2xl p-8 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`value-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#00A4A9]/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#00A4A9]" />
                  </div>
                  <h3 className="font-outfit text-xl font-medium text-[#03045e] mb-3">{t(`about.${key}`)}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{t(`about.${key}Desc`)}</p>
                </div>
              </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats removed for consistency across pages */}
    </div>
  );
}
